/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  attr,
  belongsTo,
  type AsyncBelongsTo,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import type { Type } from '@warp-drive/core/types/symbols';
import type { StatefulArray } from 'ember-data-model-fragments/array/stateful';
import { array } from 'ember-data-model-fragments/attributes';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import type SnapshotType from '@linc-technologies/linced-common/enums/snapshot-type';
import type Person from '@linc-technologies/linced-common/models/person';
import type School from '@linc-technologies/linced-common/models/school';
import moment from 'moment/moment';

// Proto: snapshots.SnapshotSet.
export default class SnapshotSet extends Model {
  declare [Type]: 'snapshots/snapshot-set';

  // Attributes
  @attr('number')
  declare createTime: number | undefined;

  @attr('number')
  declare updateTime: number | undefined;

  @computed('createTime')
  get createDate() {
    return this.createTime
      ? moment.unix(this.createTime).format('DD/MM/YYYY HH:mm')
      : '';
  }

  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  @attr('string', { defaultValue: '' })
  declare resourceId: string;

  // Metadata
  @attr('string')
  declare startDate: string | undefined;

  @attr('string')
  declare endDate: string | undefined;

  @attr('number', { defaultValue: 0 })
  declare snapshotType: EnumValue<typeof SnapshotType>;

  @attr('number', { defaultValue: 0 })
  declare term: number;

  @attr('number', { defaultValue: 0 })
  declare year: number;

  @belongsTo('person', { async: true, inverse: null })
  declare generatedById: AsyncBelongsTo<Person>;

  @alias('generatedById')
  declare generatedBy: AsyncBelongsTo<Person>;

  // no "hasMany" relationship due to ember datastore getting every snapshot in a request
  // and there are too many of them
  @array('string')
  declare snapshotIds: StatefulArray<string>;

  @array('string')
  declare learnerIds: StatefulArray<string>;

  @attr('number', { defaultValue: 0 })
  declare numSnapshotsWithoutPdf: number;

  // numSnapshotsWithPdf gives the number of snapshots that have a PDF. The
  // backend writes snapshotIds only when all the learners are done, but
  // numSnapshotsWithoutPdf counts the saved snapshots of the set. Thus a set
  // that is being generated, or whose generation stopped, can have more
  // snapshots without a PDF than snapshotIds. The count is never below 0.
  get numSnapshotsWithPdf(): number {
    return Math.max(this.snapshotIds.length - this.numSnapshotsWithoutPdf, 0);
  }
}
